/** react-query хуки «WB: регионы». retry выключен: 503 «витрина не собрана» и 422 ретраями
 *  не лечатся — ошибка видна сразу. */
import { useQuery } from "@tanstack/react-query";

import {
  getWbChecks,
  getWbChecksHistory,
  getWbInBox,
  getWbMeta,
  getWbOffer,
  getWbOffers,
  getWbRegions,
  getWbSupplies,
  getWbSupply,
  type SupplyQuery,
} from "@/api/wbRegions";

const STALE = 5 * 60 * 1000;
/** Корень ключей раздела: `invalidateQueries([WB_QUERY_ROOT])` — сброс всего кэша раздела. */
export const WB_QUERY_ROOT = "wb-regions";
const ROOT = WB_QUERY_ROOT;

export function useWbMeta() {
  return useQuery({
    queryKey: [ROOT, "meta"],
    queryFn: ({ signal }) => getWbMeta(signal),
    staleTime: STALE,
    retry: false,
    retryOnMount: false,
  });
}

export function useWbRegions(legal: string | null) {
  return useQuery({
    queryKey: [ROOT, "regions", legal],
    queryFn: ({ signal }) => getWbRegions(legal!, signal),
    staleTime: STALE,
    retry: false,
    enabled: legal !== null,
  });
}

export function useWbOffers(legal: string | null, q: string) {
  return useQuery({
    queryKey: [ROOT, "offers", legal, q],
    queryFn: ({ signal }) => getWbOffers(legal!, q || null, signal),
    staleTime: STALE,
    retry: false,
    enabled: legal !== null,
    // новый поиск у ТОГО ЖЕ кабинета — старая таблица остаётся до ответа (с пометкой «ищу…»,
    // `isPlaceholderData`); другой кабинет — загрузка с нуля, чужие цифры не показываем
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[2] === legal ? prev : undefined,
  });
}

export function useWbOffer(legal: string, offerKey: string) {
  return useQuery({
    queryKey: [ROOT, "offer", legal, offerKey],
    queryFn: ({ signal }) => getWbOffer(legal, offerKey, signal),
    staleTime: STALE,
    retry: false,
  });
}

export function useWbSupplies(legal: string | null, query: SupplyQuery | null) {
  return useQuery({
    queryKey: [ROOT, "supplies", legal, query],
    queryFn: ({ signal }) => getWbSupplies(legal!, query!, signal),
    staleTime: STALE,
    retry: false,
    enabled: legal !== null && query !== null,
    // смена фильтра у того же кабинета — старая таблица до ответа (как поиск офферов)
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[2] === legal ? prev : undefined,
  });
}

export function useWbSupply(legal: string, supplyId: number) {
  return useQuery({
    queryKey: [ROOT, "supply", legal, supplyId],
    queryFn: ({ signal }) => getWbSupply(legal, supplyId, signal),
    staleTime: STALE,
    retry: false,
  });
}

export function useWbChecks(legal: string | null) {
  return useQuery({
    queryKey: [ROOT, "checks", legal],
    queryFn: ({ signal }) => getWbChecks(legal!, signal),
    staleTime: STALE,
    retry: false,
    enabled: legal !== null,
  });
}

export function useWbChecksHistory(legal: string | null) {
  return useQuery({
    queryKey: [ROOT, "checks-history", legal],
    queryFn: ({ signal }) => getWbChecksHistory(legal!, signal),
    staleTime: STALE,
    retry: false,
    enabled: legal !== null,
  });
}

/** Квант из 1С: бэкенд держит мост в кэше 30 минут — столько же и здесь. */
export function useWbInBox() {
  return useQuery({
    queryKey: [ROOT, "in-box"],
    queryFn: ({ signal }) => getWbInBox(signal),
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
}
